/**
 * On-demand feature loading.
 *
 * __RV_LAZY_EXTRAS__ is defined `true` only for the website IIFE bundle
 * (scripts/build-widget.js). There, `./extras` is replaced by an empty stub at
 * build time and the real code is fetched as a classic <script> from the
 * ReadingVox origin on first use. Everywhere else — notably the Chrome
 * extension, which must not load remote code — extras are bundled statically.
 */

import * as bundledExtras from './extras';
import { loadRemoteScript } from './lazy-remote';

export type Extras = typeof bundledExtras;

declare const __RV_LAZY_EXTRAS__: boolean | undefined;
declare const __RV_EXTRAS_FILE__: string | undefined;

export const EXTRAS_ARE_LAZY: boolean = typeof __RV_LAZY_EXTRAS__ !== 'undefined' && !!__RV_LAZY_EXTRAS__;

let extrasOrigin = 'https://readingvox.com';
let extrasPromise: Promise<Extras> | null = null;

/** Origin the widget script was served from (set by the website entry point) */
export function setExtrasOrigin(origin: string): void {
  extrasOrigin = origin.replace(/\/+$/, '');
}

export function loadExtras(): Promise<Extras> {
  if (!EXTRAS_ARE_LAZY) return Promise.resolve(bundledExtras);
  if (extrasPromise) return extrasPromise;

  const w = window as any;
  if (w.__ReadingVoxExtras) return (extrasPromise = Promise.resolve(w.__ReadingVoxExtras));

  const file = typeof __RV_EXTRAS_FILE__ !== 'undefined' ? __RV_EXTRAS_FILE__ : 'readingvox-extras.min.js';
  extrasPromise = new Promise<Extras>((resolve, reject) => {
    w.__ReadingVoxExtrasReady = (extras: Extras) => resolve(extras);
    loadRemoteScript(`${extrasOrigin}/widget/v1/${file}`).then(() => {
      if (w.__ReadingVoxExtras) resolve(w.__ReadingVoxExtras);
      else reject(new Error('ReadingVox tools failed to initialise'));
    }, (error) => {
      extrasPromise = null; // allow retry
      reject(error);
    });
  });
  return extrasPromise;
}
